import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import './WhiteSection.css';

// Image protection handlers
const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    return false;
};

const handleDragStart = (e: any) => {
    e.preventDefault();
    return false;
};

const WhiteSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  // Helper function to wrap punctuation and numbers with default font
  const wrapWithDefaultFont = (text: string) => {
    return text.split(/([.,;:!?'"()[\]{}0-9]+)/).map((part, index) => {
      if (/[.,;:!?'"()[\]{}]/.test(part) || /\d/.test(part)) {
        return <span key={index} className="default-font">{part}</span>;
      }
      return part;
    });
  };

  // Scroll-linked transforms

  // Header animations (0.05 - 0.2)
  const headerOpacity = useTransform(scrollYProgress, [0.05, 0.2], [0, 1]);
  const headerY = useTransform(scrollYProgress, [0.05, 0.2], [50, 0]);

  // Title decoration animations (0.1 - 0.25)
  const decorationScale = useTransform(scrollYProgress, [0.1, 0.25], [0, 1]);
  const decorationOpacity = useTransform(scrollYProgress, [0.1, 0.25], [0, 1]);

  // Narrative text animations (0.15 - 0.35)
  const narrativeOpacity = useTransform(scrollYProgress, [0.15, 0.35], [0, 1]);
  const narrativeY = useTransform(scrollYProgress, [0.15, 0.35], [40, 0]);

  // Highlight text animations (0.25 - 0.45)
  const highlightOpacity = useTransform(scrollYProgress, [0.25, 0.45], [0, 1]);
  const highlightY = useTransform(scrollYProgress, [0.25, 0.45], [40, 0]);

  // Image animations (0.15 - 0.55)
  // Main image
  const mainImageOpacity = useTransform(scrollYProgress, [0.15, 0.4], [0, 1]);
  const mainImageScale = useTransform(scrollYProgress, [0.15, 0.45], [0.8, 1]);
  const mainImageRotate = useTransform(scrollYProgress, [0.15, 0.45], [5, 0]);

  // Secondary image
  const secondaryImageOpacity = useTransform(scrollYProgress, [0.25, 0.5], [0, 1]);
  const secondaryImageY = useTransform(scrollYProgress, [0.25, 0.5], [50, 0]);

  // Tertiary image
  const tertiaryImageOpacity = useTransform(scrollYProgress, [0.35, 0.55], [0, 1]);
  const tertiaryImageY = useTransform(scrollYProgress, [0.35, 0.55], [50, 0]);

  // Quote animations (0.3 - 0.8)
  const quoteOpacity = useTransform(scrollYProgress, [0.3, 0.8], [0, 1]);
  const quoteScale = useTransform(scrollYProgress, [0.3, 0.8], [0.9, 1]);
  const quoteY = useTransform(scrollYProgress, [0.3, 0.8], [30, 0]);

  return (
    <div className="white-section" id="about" ref={containerRef}>
      <div className="about-container">
        {/* Main About Content */}
        <div className="about-main">
          <div className="about-left">
            <motion.div
              className="section-header"
              style={{ opacity: headerOpacity, y: headerY }}
            >
              <h2 className="section-title">
                STORYTELLERS <span className="title-accent">AT HEART</span>
              </h2>
              <motion.div
                className="title-decoration"
                style={{ opacity: decorationOpacity, scale: decorationScale }}
              >
                <div className="decoration-line"></div>
                <div className="decoration-dot"></div>
                <div className="decoration-line"></div>
              </motion.div>
            </motion.div>

            <div className="about-narrative">
              <motion.p
                className="narrative-text"
                style={{ opacity: narrativeOpacity, y: narrativeY }}
              >
                {wrapWithDefaultFont("Welcome to our world of photography, where moments turn into timeless memories. With over a decade of experience, we specialize in capturing emotions, stories, and beauty through our lenses. Our dedication to excellence and creativity ensures that every shot we take is a masterpiece.")}
              </motion.p>
              <motion.div 
                className="why-choose-us"
                style={{ opacity: highlightOpacity, y: highlightY }}
              >
                <h3 className="why-choose-title">Why Choose Us?</h3>
                <p className="why-choose-tagline">We believe every picture tells a story</p>
                <ul className="features-list">
                  <li>We provide premium quality photo albums</li>
                  <li>We also provide exceptional quality cinematography</li>
                  <li>Passionate and skilled team dedicated to perfection</li>
                </ul>
              </motion.div>
            </div>
          </div>

          <div className="about-right">
            <div className="image-showcase">
              <div className="showcase-grid">
                <motion.div
                  className="showcase-item main"
                  style={{
                    opacity: mainImageOpacity,
                    scale: mainImageScale,
                    rotate: mainImageRotate
                  }}
                >
                  <motion.div
                    className="image-showcase-item"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785923/08_cd7imp_xsfmaq.webp"
                      alt="Cinematic wedding moment"
                      className="showcase-image"
                      onContextMenu={handleContextMenu}
                      onDragStart={handleDragStart}
                      style={{ pointerEvents: 'none' }}
                    />
                  </motion.div>
                </motion.div>
                <motion.div
                  className="showcase-item secondary"
                  style={{
                    opacity: secondaryImageOpacity,
                    y: secondaryImageY
                  }}
                >
                  <motion.div
                    className="image-showcase-item"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="https://res.cloudinary.com/dhqmcmekn/image/upload/v1764754929/_DSC6912-Edited_tb14jv.webp"
                      alt="Love story moment"
                      className="showcase-image"
                      onContextMenu={handleContextMenu}
                      onDragStart={handleDragStart}
                      style={{ pointerEvents: 'none' }}
                    />
                  </motion.div>
                </motion.div>
                <motion.div
                  className="showcase-item tertiary"
                  style={{
                    opacity: tertiaryImageOpacity,
                    y: tertiaryImageY
                  }}
                >
                  <motion.div
                    className="image-showcase-item"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.3 }}
                  >
                    <img
                      src="https://res.cloudinary.com/dhqmcmekn/image/upload/v1764785924/09_ucn0w4_yfoyrz.webp"
                      alt="Timeless wedding moment"
                      className="showcase-image"
                      onContextMenu={handleContextMenu}
                      onDragStart={handleDragStart}
                      style={{ pointerEvents: 'none' }}
                    />
                  </motion.div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>

        {/* Hero Quote Section - Moved to Bottom */}
        <motion.div
          className="hero-quote"
          style={{
            opacity: quoteOpacity,
            scale: quoteScale,
            y: quoteY
          }}
        >
          <div className="quote-mark opening">"</div>
          <p className="quote-text">
            {wrapWithDefaultFont("We don't just film weddings. We preserve the heartbeat of your love story, frame by frame, creating cinematic heirlooms that whisper your names across generations.")}
          </p>
          <div className="quote-mark closing">"</div>
          <div className="quote-attribution">
            — Studio Z26 Philosophy
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WhiteSection;
